import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServiceEventsApi } from './events';
import { ServiceLogsApi } from './logs';
import { ServiceMetricsApi } from './metrics';
import type { JobExecution, QueryOptions, SearchOptions, Service, ServiceCreateInput, ServiceSecret, WebhookRegenerateResponse } from '@partiri-cloud/types';
import { WaitingListGuard } from '../waiting-list.guard';

/**
 * @file services.ts
 * Class for the services client
 */
export class ServicesApi {
  private endpoint = '/services'

  /** SUBMODULES **/
  readonly events: ServiceEventsApi;
  readonly logs: ServiceLogsApi;
  readonly metrics: ServiceMetricsApi;

  constructor(private http: HttpClient) {
    this.events = new ServiceEventsApi(http);
    this.logs = new ServiceLogsApi(http);
    this.metrics = new ServiceMetricsApi(http);
  }

  /**
   * List all the services
   * @returns Service[]
   */
  public list(options: QueryOptions): Observable<Service[]> {
    let params = new HttpParams().set('project', options.project);
    if (options.limit != null) params = params.set('limit', options.limit);
    return this.http.get<Service[]>(this.endpoint, { params })
  }

  /**
   * Read a single service
   * @param id 
   * @returns Service
   */
  public read(id: string): Observable<Service> {
    return this.http.get<Service>(`${this.endpoint}/${id}`)
  }

  /**
   * Per-execution billing rows for a batch service (deploy_type 'cronjob').
   * Rendered in the Executions tab of the service detail view.
   */
  public listExecutions(
    serviceId: string,
    options?: { limit?: number; offset?: number },
  ): Observable<{ data: JobExecution[]; total: number }> {
    let params = new HttpParams();
    if (options?.limit != null) params = params.set('limit', options.limit);
    if (options?.offset != null) params = params.set('offset', options.offset);
    return this.http.get<{ data: JobExecution[]; total: number }>(
      `${this.endpoint}/${serviceId}/executions`,
      { params },
    )
  }

  /**
   * Create a new service
   * @param service
   */
  @WaitingListGuard()
  public create(service: ServiceCreateInput): Observable<Service> {
    return this.http.post<Service>(`${this.endpoint}`, service)
  }

  /**
   * Update an existing service
   * @param id
   * @param project
   */
  @WaitingListGuard()
  public update(id: string, service: ServiceCreateInput): Observable<Service> {
    return this.http.put<Service>(`${this.endpoint}/${id}`, service)
  }

  /**
   * Delete a service
   * @param id 
  */
  @WaitingListGuard()
  public delete(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.endpoint}/${id}`)
  }

  /**
   * Search services by name within a project
   * @param project
   * @param searchOptions
  */
  public search(project: string, searchOptions: SearchOptions): Observable<Service[]> {
    let params = new HttpParams().set('project', project);
    if (searchOptions.column) params = params.set('column', searchOptions.column);
    if (searchOptions.value) params = params.set('value', searchOptions.value);
    if (searchOptions.limit != null) params = params.set('limit', searchOptions.limit);
    if (searchOptions.offset != null) params = params.set('offset', searchOptions.offset);
    return this.http.get<Service[]>(`${this.endpoint}/search`, { params })
  }

  /**
   * List a service's env secrets. Metadata only — the API returns no value on
   * any route, so there is nothing here to reveal.
   */
  public listServiceSecrets(serviceId: string): Observable<ServiceSecret[]> {
    return this.http.get<ServiceSecret[]>(`${this.endpoint}/${serviceId}/secrets`)
  }

  /**
   * Create a service env secret. `name` is the environment variable name; it
   * takes effect on the service's next deploy.
   */
  @WaitingListGuard()
  public createServiceSecret(serviceId: string, secret: Required<Pick<ServiceSecret, 'name' | 'data'>>): Observable<ServiceSecret> {
    return this.http.post<ServiceSecret>(`${this.endpoint}/${serviceId}/secrets`, secret)
  }

  /**
   * Rotate a secret's value. The name is immutable — renaming means deleting and
   * re-creating.
   */
  @WaitingListGuard()
  public updateServiceSecret(serviceId: string, secretId: string, value: string): Observable<ServiceSecret> {
    return this.http.put<ServiceSecret>(`${this.endpoint}/${serviceId}/secrets/${secretId}`, { data: { value } })
  }

  /**
   * Delete a service env secret.
   */
  @WaitingListGuard()
  public deleteServiceSecret(serviceId: string, secretId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.endpoint}/${serviceId}/secrets/${secretId}`)
  }

  /**
   * Check if a webhook exists for a service
   * @param serviceId
   * @returns
   */
  public checkWebhook(serviceId: string): Observable<{ exists: boolean }> {
    return this.http.get<{ exists: boolean }>(`/webhooks/${serviceId}`)
  }

  /**
   * Generate or regenerate webhook. Returns { url, secret } as separate
   * fields — the secret is shown once, and callers paste it into the git
   * provider's "Secret" field for HMAC validation.
   * @param serviceId
   * @returns
   */
  public regenerateWebhook(serviceId: string): Observable<WebhookRegenerateResponse> {
    return this.http.post<WebhookRegenerateResponse>(`/webhooks/${serviceId}/regenerate`, {})
  }

}
